import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";
import { Avtal, AvtalOption } from "@/types";

type State = BaseState & {
    avtal: Map<Avtal["id"], Avtal>;
};

export const convertAvtalData: (args: { avtal?: Avtal }) => AvtalOption | null = ({ avtal }) => {
    if (!avtal) {
        return null;
    }

    return {
        value: avtal.id,
        label: avtal.namn,
        typ: avtal.typ,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        avtal: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "avtal";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        avtal: (state: State) => [...state.avtal.values()],
        avtalSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertAvtalData({ avtal: singleResult }));
            }

            return result;
        },
        avtalById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.avtal.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addAvtal: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                avtal = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                avtal: Avtal[];
            }
        ) =>
            subsetSetter<State, Avtal>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: avtal,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchAvtal: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Avtal>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "core/avtal/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addAvtal", {
                    subsetName,
                    offset: resultOffset,
                    avtal: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertAvtalData({ avtal: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
